import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { publicDoctorApi } from '../../api/doctorApi';

// Async thunk for fetching public doctor profile
export const fetchPublicDoctorProfile = createAsyncThunk(
  'publicDoctor/fetchProfile',
  async (doctorId, { rejectWithValue }) => {
    try {
      const response = await publicDoctorApi.getPublicProfile(doctorId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch doctor profile');
    }
  }
);

// Async thunk for fetching doctor availability
export const fetchDoctorAvailability = createAsyncThunk(
  'publicDoctor/fetchAvailability',
  async ({ doctorId, date }, { rejectWithValue }) => {
    try {
      const response = await publicDoctorApi.getAvailability(doctorId, date);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch availability');
    }
  }
);

const initialState = {
  currentDoctor: null,
  loading: false,
  error: null,
  availability: null,
  availabilityLoading: false,
  availabilityError: null,
};

const publicDoctorSlice = createSlice({
  name: 'publicDoctor',
  initialState,
  reducers: {
    clearCurrentDoctor: (state) => {
      state.currentDoctor = null;
      state.error = null;
    },
    clearAvailability: (state) => {
      state.availability = null;
      state.availabilityError = null;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch public doctor profile
      .addCase(fetchPublicDoctorProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPublicDoctorProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDoctor = action.payload;
        state.error = null;
      })
      .addCase(fetchPublicDoctorProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch doctor availability
      .addCase(fetchDoctorAvailability.pending, (state) => {
        state.availabilityLoading = true;
        state.availabilityError = null;
      })
      .addCase(fetchDoctorAvailability.fulfilled, (state, action) => {
        state.availabilityLoading = false;
        state.availability = action.payload;
        state.availabilityError = null;
      })
      .addCase(fetchDoctorAvailability.rejected, (state, action) => {
        state.availabilityLoading = false;
        state.availabilityError = action.payload;
      });
  },
});

export const { clearCurrentDoctor, clearAvailability, setError } = publicDoctorSlice.actions;

// Selectors
export const selectCurrentDoctor = (state) => state.publicDoctor.currentDoctor;
export const selectDoctorLoading = (state) => state.publicDoctor.loading;
export const selectDoctorError = (state) => state.publicDoctor.error;
export const selectDoctorAvailability = (state) => state.publicDoctor.availability;
export const selectAvailabilityLoading = (state) => state.publicDoctor.availabilityLoading;
export const selectAvailabilityError = (state) => state.publicDoctor.availabilityError;

export default publicDoctorSlice.reducer; 